/**
 * wireWaDialog — attach a `<wa-dialog>` to BOTH a React ref and its close event.
 *
 * WHY THIS EXISTS (bug found in production 2026-08-05, shipped 2026-07-22 in
 * `21b0ce9` and broken every day since).
 *
 * `DeliverDialog` and `QuickAddTaskDialog` each did this:
 *
 *     const dialogRef = useRef(null);
 *     useEffect(() => { if (dialogRef.current) dialogRef.current.open = open; }, [open]);
 *     ...
 *     <wa-dialog ref={wireDialogClose(onClose)}>      // <-- NOT dialogRef
 *
 * `wireDialogClose` returned a callback ref that wired the `wa-after-hide`
 * listener and **never assigned `dialogRef.current`**. So the ref stayed null
 * forever, the effect's `if (dialogRef.current)` guard silently no-opped, and
 * `.open` was never set — the dialogs NEVER OPENED. Clicking "Deliver" mounted
 * a component that rendered nothing visible.
 *
 * The consequence was not cosmetic: Deliver is the ONE gate across the
 * captured→delivered wall, so the load-bearing transition of the whole Song
 * Notes arc had never once been executed. Confirmed against production — all 10
 * real song notes sat at `captured` with zero history entries past it, and the
 * family-facing Songs page (which renders only focus/landed) could therefore
 * never show a real family a real note.
 *
 * THE FAILURE MODE IS SILENT BY CONSTRUCTION, which is why it survived a full
 * redesign, a test suite, and a live QA pass: a `useRef` read behind an
 * `if (ref.current)` guard produces no error, no warning, and no test failure
 * when the ref is never attached. **Grep for `ref={` on the ELEMENT, never for
 * the ref's declaration.**
 *
 * Both call sites now share this helper so a third copy cannot drift. It does
 * the two things that must happen together and were previously done apart.
 */

/**
 * @param {React.MutableRefObject<HTMLElement|null>} ref - receives the element
 * @param {Function} onClose - called on `wa-after-hide` (ESC / backdrop / close)
 * @returns {Function} a callback ref for the `<wa-dialog>` element
 */
const wireWaDialog = (ref, onClose) => (el) => {
	// The ref assignment is the half that was missing. React calls a callback
	// ref with null on unmount (and with null-then-element across re-renders
	// when the callback identity changes), so mirror that faithfully rather
	// than only assigning on the truthy branch.
	if (ref) ref.current = el || null;

	if (el && onClose && !el.__waCloseWired) {
		// React 18 does not wire custom-element events, so an onWaHide prop
		// would be dead — the listener has to be attached imperatively. The
		// flag keeps it to one listener across re-renders.
		el.__waCloseWired = true;
		el.addEventListener('wa-after-hide', onClose);
	}
};

export default wireWaDialog;
